import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Check, ChevronLeft, ChevronRight, Send, X } from 'lucide-react';
import { STEPS, DEFAULT_CONFIG, enclosureTypes, glassTypes, hardwareFinishes, handleStyles, coatingOptions } from './shower-designer/constants';
import EnclosureStep from './shower-designer/steps/EnclosureStep';
import DimensionsStep from './shower-designer/steps/DimensionsStep';
import GlassStep from './shower-designer/steps/GlassStep';
import HardwareStep from './shower-designer/steps/HardwareStep';
import ContactStep from './shower-designer/steps/ContactStep';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ShowerDesigner = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formLoadedAt] = useState(() => Date.now());
  const [honeypot, setHoneypot] = useState('');
  const [config, setConfig] = useState({ ...DEFAULT_CONFIG });

  // Auto-calculate panel width
  useEffect(() => {
    if (config.enclosureType && config.enclosureType !== 'single-door' && config.enclosureType !== 'neo-angle') {
      const width = parseFloat(config.dimensions.width) || 0;
      const doorWidth = parseFloat(config.dimensions.doorWidth) || 28;
      if (width > doorWidth) {
        setConfig(prev => ({
          ...prev,
          dimensions: { ...prev.dimensions, panelWidth: (width - doorWidth).toString() }
        }));
      }
    }
  }, [config.dimensions.width, config.dimensions.doorWidth, config.enclosureType]);

  const updateConfig = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));
  const updateDimensions = (field, value) => setConfig(prev => ({ ...prev, dimensions: { ...prev.dimensions, [field]: value } }));
  const updateContact = (field, value) => setConfig(prev => ({ ...prev, contact: { ...prev.contact, [field]: value } }));

  const canProceed = () => {
    switch (currentStep) {
      case 1: return config.enclosureType !== '';
      case 2:
        if (config.isComplex) return true;
        if (config.enclosureType === 'neo-angle') return config.dimensions.side1 && config.dimensions.front && config.dimensions.side2 && config.dimensions.height;
        return config.dimensions.width && config.dimensions.height;
      case 3: return config.glassType && config.glassThickness;
      case 4: return config.hardwareFinish && config.handleStyle;
      case 5: return config.contact.name && config.contact.email && config.contact.phone;
      default: return true;
    }
  };

  const handleSubmit = async () => {
    if (!canProceed()) return;
    setIsSubmitting(true);

    const enclosure = enclosureTypes.find(e => e.id === config.enclosureType);
    const glass = glassTypes.find(g => g.id === config.glassType);
    const finish = hardwareFinishes.find(h => h.id === config.hardwareFinish);
    const handle = handleStyles.find(h => h.id === config.handleStyle);
    const coating = coatingOptions.find(c => c.id === config.coating);

    let dimensionsText = '';
    if (config.isComplex) {
      dimensionsText = 'Complex installation - customer will provide details during consultation';
    } else if (config.enclosureType === 'neo-angle') {
      dimensionsText = `- Side 1: ${config.dimensions.side1}"\n- Front (Door): ${config.dimensions.front}"\n- Side 2: ${config.dimensions.side2}"\n- Height: ${config.dimensions.height}"`;
    } else {
      dimensionsText = `- Overall Width: ${config.dimensions.width}"\n- Height: ${config.dimensions.height}"\n${config.dimensions.doorWidth ? `- Door Width: ${config.dimensions.doorWidth}"` : ''}\n${config.dimensions.panelWidth ? `- Panel Width: ${config.dimensions.panelWidth}"` : ''}`;
    }

    const designSummary = `
SHOWER GLASS DESIGN REQUEST
============================

ENCLOSURE TYPE: ${enclosure?.name || config.enclosureType}
- ${enclosure?.description || ''}
${config.enclosureType !== 'sliding' && config.enclosureType !== 'neo-angle' ? `- Hinge Position: ${config.hingePosition === 'left' ? 'Left' : 'Right'}` : ''}
${config.enclosureType === 'door-panel' ? `- Door Position: ${config.doorPosition === 'left' ? 'Left of Panel' : 'Right of Panel'}` : ''}

DIMENSIONS:
${dimensionsText}

GLASS SELECTION:
- Type: ${glass?.name || config.glassType}
- Thickness: ${config.glassThickness}"
- Coating: ${coating?.name || 'Standard'}

HARDWARE:
- Finish: ${finish?.name || config.hardwareFinish}
- Handle Style: ${handle?.name || config.handleStyle}

TIMELINE: ${config.contact.timeline || 'Not specified'}

ADDITIONAL NOTES:
${config.additionalNotes || 'None'}
    `.trim();

    const formData = {
      name: config.contact.name,
      email: config.contact.email,
      phone: config.contact.phone,
      message: designSummary,
      website: honeypot,
      formLoadedAt,
    };

    // Try API first
    if (BACKEND_URL) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (response.ok) {
          toast.success('Design submitted!', { description: "We'll contact you within 24 hours to discuss your shower glass project." });
          onClose();
          resetForm();
          return;
        }
      } catch (error) {
        console.log('API not available, trying fallback');
      }
    }

    // Fallback to Netlify Forms
    try {
      const formBody = new URLSearchParams({
        'form-name': 'contact',
        name: config.contact.name,
        email: config.contact.email,
        phone: config.contact.phone,
        message: designSummary,
      }).toString();
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody,
      });
      if (response.ok) {
        toast.success('Design submitted!', { description: "We'll contact you within 24 hours to discuss your shower glass project." });
        onClose();
        resetForm();
        return;
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }

    toast.error('Unable to submit', { description: 'Please call us at (830) 359-9925 to discuss your project.' });
    setIsSubmitting(false);
  };

  const resetForm = () => {
    setCurrentStep(1);
    setIsSubmitting(false);
    setConfig({ ...DEFAULT_CONFIG });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-cyan-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">Shower Glass Designer</h2>
              <p className="text-white/80 text-sm mt-1">Design your perfect shower enclosure</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors" data-testid="designer-close">
              <X className="h-6 w-6" />
            </button>
          </div>
          {/* Progress Steps */}
          <div className="flex items-center justify-between mt-6">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                  currentStep > step.id ? 'bg-white text-primary' : currentStep === step.id ? 'bg-white text-primary' : 'bg-white/30 text-white'
                }`}>
                  {currentStep > step.id ? <Check className="h-4 w-4" /> : step.id}
                </div>
                <span className={`ml-2 text-sm hidden sm:block ${currentStep >= step.id ? 'text-white' : 'text-white/60'}`}>{step.name}</span>
                {index < STEPS.length - 1 && <div className={`w-8 sm:w-16 h-0.5 mx-2 ${currentStep > step.id ? 'bg-white' : 'bg-white/30'}`} />}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {currentStep === 1 && <EnclosureStep config={config} updateConfig={updateConfig} />}
          {currentStep === 2 && <DimensionsStep config={config} updateConfig={updateConfig} updateDimensions={updateDimensions} />}
          {currentStep === 3 && <GlassStep config={config} updateConfig={updateConfig} />}
          {currentStep === 4 && <HardwareStep config={config} updateConfig={updateConfig} />}
          {currentStep === 5 && <ContactStep config={config} updateContact={updateContact} honeypot={honeypot} setHoneypot={setHoneypot} />}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-between items-center bg-gray-50">
          <Button variant="outline" onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))} disabled={currentStep === 1} data-testid="designer-back">
            <ChevronLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          {currentStep < 5 ? (
            <Button onClick={() => setCurrentStep(prev => Math.min(5, prev + 1))} disabled={!canProceed()} data-testid="designer-continue">
              Continue <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={!canProceed() || isSubmitting} className="bg-primary hover:bg-primary/90" data-testid="designer-submit">
              {isSubmitting ? 'Submitting...' : 'Submit Design Request'} <Send className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShowerDesigner;
